import React, { useContext, useEffect, useRef } from 'react';

import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ReactComponent as OutIcon } from '../../../../../assets/icons/v3/out.svg';
import userDefaultIcon from '../../../../../assets/icons/v3/user-picture.svg?url';
import BdsCircleIndicatorPictogram from '../../../../../shared/components/v3/BdsCircleIndicator/BdsCircleIndicatorPictogram/BdsCircleIndicatorPictogram';
import InfiniteScroll from '../../../../../shared/components/v3/InfiniteScroll/InfiniteScroll';
import TableDetailsHero from '../../../../../shared/components/v3/Table/TableDetailsHero/TableDetailsHero';
import { CHALLENGE_LEADERBOARD_PAGE_SIZE } from '../../../../../shared/constants/challenges';
import { CONTROLLER_ON } from '../../../../../shared/constants/colors';
import { CHALLENGES_ICON } from '../../../../../shared/constants/images';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import useOutsideComponentClick from '../../../../../shared/hooks/useOutsideComponentClick/outsideComponentClick';
import { IChallengeDetailProps, IChallengeLeaderboardItem } from '../../../../../shared/interfaces/challenges';
import Challenges from '../../../../../shared/services/api/challenges/challenges';
import { TRANSLATION_CHALLENGES, TRANSLATIONS_CHALLENGES_MAIN } from '../../../../../shared/translations/challenges';
import { daysUntil } from '../../../../../shared/utils/datetime/datetime';
import { toLocaleNumberFormat } from '../../../../../shared/utils/utils';
import './ChallengeDetail.scss';

const ChallengeDetail = ({ open, challenge, onClose }: IChallengeDetailProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const detailContainerRef = useRef<HTMLDivElement>(null);
  const { company } = useContext(CompanyContext);
  const { t } = useTranslation();

  const getLeaderboard = async (limit: number, offset: number) => {
    if (!open || !challenge.leaderboardId) {
      return { items: [], total: 0 };
    }
    const response = await Challenges.getLeaderboard(company.id, challenge.leaderboardId, limit, offset);
    return { items: response, total: challenge.participants };
  };

  const renderLeaderboardItem = (leader: IChallengeLeaderboardItem) => (
    <div key={leader.id} className="leaderboard-item">
      <div className={`leaderboard-item-position position-${leader.position}`}>{leader.position}</div>
      <img
        src={leader.icon && leader.icon !== '' ? leader.icon : userDefaultIcon}
        alt={leader.name}
        className="leaderboard-item-avatar"
      />
      <div className="leaderboard-item-name">{leader.name}</div>
      <div className="leaderboard-item-score">{toLocaleNumberFormat(leader.score)}</div>
    </div>
  );

  useEffect(() => {
    if (detailContainerRef.current) {
      detailContainerRef.current.scrollTop = 0;
    }
  }, [challenge]);

  useOutsideComponentClick(detailContainerRef as React.RefObject<HTMLElement>, onClose);

  return (
    <div className={`challenge-detail-container ${open ? 'open' : ''}`} aria-expanded={open} ref={detailContainerRef}>
      <TableDetailsHero
        open={open}
        icon={
          <BdsCircleIndicatorPictogram
            circleIndicatorSize="l"
            pictogram={challenge.iconUrl ? { url: challenge.iconUrl } : { component: CHALLENGES_ICON }}
            className="challenge-detail-icon"
          />
        }
        title={challenge.name}
        description={challenge.description}
        status={challenge.status}
        statusLabel={t(TRANSLATIONS_CHALLENGES_MAIN.table.status[challenge.status])}
        dateLabel={
          daysUntil(challenge.endAt) > 0
            ? t(TRANSLATION_CHALLENGES.detail.daysLeft, { days: daysUntil(challenge.endAt) })
            : undefined
        }
        onClose={onClose}
      />
      <div className="challenge-detail" ref={ref}>
        <div className="challenge-detail-participants">
          <div className="challenge-detail-participants-title">
            {t(TRANSLATION_CHALLENGES.detail.participants, {
              count: challenge.participants,
              users: challenge.participants
            })}
          </div>
          {challenge.participantsPercentageOverAverage > 0 && (
            <div className="challenge-detail-participants-insight">
              <OutIcon className="insight-icon" />
              {t(TRANSLATION_CHALLENGES.detail.insight, { metric: `${challenge.participantsPercentageOverAverage}%` })}
            </div>
          )}
        </div>
        <div className="challenge-detail-leaderboard" ref={ref}>
          <InfiniteScroll<IChallengeLeaderboardItem>
            key={challenge.leaderboardId || challenge.id}
            fetchPage={getLeaderboard}
            limit={CHALLENGE_LEADERBOARD_PAGE_SIZE}
            renderItem={renderLeaderboardItem}
            loadingState={
              <div className="leaderboard-loading">
                <CircularProgress sx={{ color: CONTROLLER_ON }} size={24} />
                <div>{t(TRANSLATION_CHALLENGES.detail.listLoading)}</div>
              </div>
            }
            finishedState={<div className="leaderboard-finished">{t(TRANSLATION_CHALLENGES.detail.listLoaded)}</div>}
          />
        </div>
      </div>
    </div>
  );
};

export default ChallengeDetail;
